<template>
  <div class="relative" ref="select">
    <div class="flex items-end">
      <FGroup :label="$t('choose_coins')" class="max-w-[450px]">
        <FInput
          @click.stop="showCoins"
          :placeholder="$t('enter_name')"
          v-model="search"
          class="!border-gray-800"
          :error="error"
          v-bind="{ disabled }"
        >
          <template #prefix>
            <i class="icon-search-normal text-xl text-gray-50 mr-3" />
          </template>
        </FInput>
      </FGroup>
    </div>
    <Transition name="dropdown-search">
      <div
        v-if="isFocus"
        v-on-click-outside.bubble="closeModal"
        :class="fromTop ? 'bottom-[65px]' : 'top-full'"
        class="bg-white border border-white-100 rounded-xl absolute left-0 w-full z-10 max-h-[245px] max-w-[450px] overflow-y-auto coin-select-shadow"
      >
        <Transition name="fade" mode="out-in">
          <div :key="loading">
            <template v-if="!loading && coins?.length">
              <template v-for="(option, i) in coins" :key="i">
                <template
                  v-if="!user?.plan?.allowed_coins?.includes(option?.id)"
                >
                  <RouterLink to="/plan">
                    <button
                      :class="{
                        'opacity-50': !user?.plan?.allowed_coins?.includes(
                          option?.id,
                        ),
                      }"
                      class="px-3 py-2 w-full border-b border-gray-800 hover:bg-gray-200 cursor-pointer transition-300 flex-center-between"
                    >
                      <div class="flex-y-center gap-2">
                        <CAvatar :image="option?.image_url" class="!w-6 !h-6" />
                        <p
                          class="text-sm leading-130 text-dark-100 font-medium"
                        >
                          {{ option?.title }}
                        </p>
                      </div>

                      <i
                        class="icon-wallet text-lg text-gray transition-300 hover:text-green"
                      />
                    </button>
                  </RouterLink>
                </template>
                <template v-else>
                  <div
                    class="px-3 py-2 w-full border-b border-gray-800 hover:bg-gray-200 cursor-pointer transition-300 flex-center-between"
                    @click="chooseOption(option)"
                  >
                    <div class="flex-y-center gap-2">
                      <CAvatar :image="option?.image_url" class="!w-6 !h-6" />
                      <p class="text-sm leading-130 text-dark-100 font-medium">
                        {{ option?.title }}
                      </p>
                    </div>

                    <div
                      v-if="user?.plan?.allowed_coins?.includes(option?.id)"
                      class="w-5 h-5 rounded-full border-2 border-gray-50 flex-center transition-300"
                      :class="{
                        'bg-green !border-green': defaultOptions?.includes(
                          option?.id,
                        ),
                      }"
                    >
                      <i
                        class="icon-tick-square text-[10px] text-white opacity-0 transition-300"
                        :class="{
                          '!opacity-100': defaultOptions?.includes(option?.id),
                        }"
                      />
                    </div>
                    <div v-else>
                      <RouterLink to="/plan">
                        <i
                          class="icon-wallet text-lg text-gray transition-300 hover:text-green"
                        />
                      </RouterLink>
                    </div>
                  </div>
                </template>
              </template>
            </template>
            <template v-else-if="!loading && !coins?.length">
              <div class="p-3 w-full">
                <p class="text-base leading-5 font-semibold text-dark">
                  {{ $t("no_results") }}
                </p>
              </div>
            </template>
            <template v-else>
              <div class="p-16 flex-center">
                <div class="spinner" />
              </div>
            </template>
          </div>
        </Transition>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import FInput from "@/components/Form/Input/FInput.vue";
import CAvatar from "@/components/CAvatar.vue";
import { nextTick, onMounted, ref, watch } from "vue";
import FGroup from "@/components/Form/FGroup.vue";
import ApiService from "@/services/ApiService";
import { debounce } from "@/utils";
import { computed } from "vue";
import { useAuthStore } from "@/modules/Auth/stores";
import { useRouter } from "vue-router";
import { vOnClickOutside } from "@vueuse/components";
import type { OnClickOutsideHandler } from "@vueuse/core";

interface Props {
  defaultOptions: any[];
  error?: boolean;
  disabled?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits(["choose-option", "remove-option"]);
const user = computed(() => useAuthStore().user);
const router = useRouter();

const loading = ref(false);
const isFocus = ref(false);
const coins = ref<any>([]);
const search = ref("");

const fromTop = ref(false);
const select = ref();

const closeModal: OnClickOutsideHandler = () => {
  isFocus.value = false;
};
function getCoins() {
  ApiService.query("/bot/common/coins", {
    params: {
      size: 100,
    },
  }).then((res) => {
    coins.value = res.data?.results;
  });
}

getCoins();

const showCoins = () => {
  if (!props?.disabled) {
    isFocus.value = true;
  }
};

function chooseOption(option: any) {
  console.log(option, "option");
  if (option === "link") {
    router.push({ name: "PlanPage" });
  } else {
    if (!props.defaultOptions?.includes(option?.id)) {
      emit("choose-option", option);
    } else {
      emit("remove-option", option);
    }
  }
}
function checkTop() {
  const bounds = select.value.getBoundingClientRect();
  const distanceToBottom = window.innerHeight - bounds.bottom;
  const threshold = 300;
  if (distanceToBottom < threshold) {
    fromTop.value = true;
  } else {
    fromTop.value = false;
  }
}
onMounted(() => {
  nextTick(() => {
    checkTop();
  });
});

watch(
  () => search.value,
  () => {
    debounce("search-coin", () => {
      loading.value = true;
      ApiService.query("/bot/common/coins", {
        params: { search: search.value, size: 100 },
      })
        .then((res: any) => {
          coins.value = res.data?.results;
        })
        .finally(() => (loading.value = false));
    });
  },
);
</script>

<style scoped>
.coin-select-shadow {
  box-shadow: 0 0 50px 0 rgba(8, 10, 21, 0.15);
}

.dropdown-search-enter-active {
  animation: dropdown-search 300ms ease-out;
}

.dropdown-search-leave-active {
  animation: dropdown-search 300ms ease-in reverse;
}

@keyframes dropdown-search {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

.spinner {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background:
    radial-gradient(farthest-side, #59debe 94%, #0000) top/3.8px 3.8px no-repeat,
    conic-gradient(#0000 30%, #59debe);
  -webkit-mask: radial-gradient(
    farthest-side,
    #0000 calc(100% - 3.8px),
    #000 0
  );
  animation: spinner-c7wet2 1s infinite linear;
}

@keyframes spinner-c7wet2 {
  100% {
    transform: rotate(1turn);
  }
}
</style>
